const MEDCALL2_ROUTE_BASE = '/tools/medcall2';
export const MEDCALL_LEGACY_TIMELINE_PAGE_SIZE = 10;

const LEGACY_READ_ONLY_TIMELINE_PROGRAMS = new Set([
  'DSH ERC',
  'FIST ERC',
  'ODRH Scattered/ERC',
  'Project Sites',
]);

const LEGACY_SCATTERED_ERC_PROGRAMS = new Set([
  'DSH ERC',
  'FIST ERC',
  'ODRH Scattered/ERC',
]);

type MedicationHistoryVisibility = {
  embedded: boolean;
  program: string;
  route: string;
  routeBase: string;
  timelineCount: number;
};

export const shouldShowMedcallMedicationHistory = ({
  embedded,
  program,
  route,
  routeBase,
  timelineCount,
}: MedicationHistoryVisibility) => {
  if (routeBase !== MEDCALL2_ROUTE_BASE && !embedded && timelineCount < 1) {
    return false;
  }

  if (routeBase !== MEDCALL2_ROUTE_BASE) {
    return program !== 'Project Sites';
  }

  if (route === 'IM-LAI') {
    return true;
  }

  return !LEGACY_READ_ONLY_TIMELINE_PROGRAMS.has(program);
};

export const userCanEditMedcallTimelineStatus = (
  routeBase: string,
  hasFullAccess: boolean,
  canManageMedications: boolean,
) => {
  if (routeBase === MEDCALL2_ROUTE_BASE) {
    return hasFullAccess;
  }

  return canManageMedications;
};

export const userCanEditMedcallTimelineWitness = (
  routeBase: string,
  canManageMedications: boolean,
) => {
  if (routeBase === MEDCALL2_ROUTE_BASE) {
    return true;
  }

  return canManageMedications;
};

export const userCanEditMedcallTimelineSite = (
  routeBase: string,
  hasFullAccess: boolean,
  canManageMedications: boolean,
) => {
  if (routeBase === MEDCALL2_ROUTE_BASE) {
    return hasFullAccess;
  }

  return canManageMedications;
};

export const isLegacyMedcallMedicationRunOut = (
  refills: Array<{ refill_date?: string | null }> | null | undefined,
  today: string,
) => {
  if (!Array.isArray(refills)) {
    return false;
  }

  return refills.some((refill) => {
    const refillDate = `${refill?.refill_date || ''}`.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || '';
    return Boolean(refillDate && refillDate < today);
  });
};

type TimelineRow = {
  date?: string | null;
  status?: string | null;
};

type MedicationFrequency = {
  days_of_week?: Array<string | null> | null;
  frequency?: string | null;
  times_per_day?: string | null;
};

type MedicationWarning = {
  injection_info?: Array<{
    date_due?: string | null;
    date_given?: string | null;
  } | null> | null;
  program: string;
  refill_status?: boolean | number | null;
  refills?: Array<{ refill_date?: string | null }> | null;
  today: string;
};

const getDateOnly = (value: unknown) => `${value || ''}`.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || '';

export const getLegacyMedcallMedicationWarning = ({
  injection_info: injectionInfo,
  program,
  refill_status: refillStatus,
  refills,
  today,
}: MedicationWarning) => {
  if (
    !LEGACY_SCATTERED_ERC_PROGRAMS.has(program)
    && Boolean(refillStatus)
    && isLegacyMedcallMedicationRunOut(refills, today)
  ) {
    return 'This medicine has run out';
  }

  const currentInjection = Array.isArray(injectionInfo) ? injectionInfo[0] : null;
  const dueDate = getDateOnly(currentInjection?.date_due);
  if (dueDate && dueDate < today && !currentInjection?.date_given) {
    return 'This medication is due and has not been administered';
  }

  const runsOutToday = Array.isArray(refills)
    && refills.some((refill) => getDateOnly(refill?.refill_date) === today);
  if (runsOutToday) {
    return 'This medicine runs out today';
  }

  return null;
};

export const getLegacyMedcallTimelineSubmissionTime = (value: Date = new Date()) => {
  const submittedTime = new Date(value.getTime() + (15 * 60 * 1000));
  const pad = (part: number) => `${part}`.padStart(2, '0');
  return [
    pad(submittedTime.getHours()),
    pad(submittedTime.getMinutes()),
    pad(submittedTime.getSeconds()),
  ].join(':');
};

export const didMedcall2TimelineMutationComplete = (
  routeBase: string,
  data: unknown,
  fieldName: string,
) => {
  if (routeBase !== MEDCALL2_ROUTE_BASE || !data || typeof data !== 'object') {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(data, fieldName);
};

export const getLegacyMedcallTimelineFrequencyLabel = (medication: MedicationFrequency) => {
  const frequency = `${medication.frequency || ''}`;
  let label = frequency;
  if (frequency === 'everyotherday') {
    label = 'QOD';
  }

  if (frequency === 'everyday') {
    label = `${medication.times_per_day || ''}`;
  }

  if (frequency === 'custom_days') {
    const days = Array.isArray(medication.days_of_week)
      ? medication.days_of_week.filter(Boolean).join(', ')
      : '';
    label = `Custom Week Days: ${days} - ${medication.times_per_day || ''}`;
  }

  return label.toUpperCase();
};

export const getVisibleMedcallTimelineRows = <Timeline extends TimelineRow>(
  timelines: Timeline[],
  route: string,
  routeBase: string,
) => {
  if (routeBase !== MEDCALL2_ROUTE_BASE) {
    return timelines;
  }

  const visibleRows = timelines.filter((timeline) => {
    if (route === 'IM-LAI') {
      return timeline.status === 'injection';
    }

    return timeline.status !== 'refill';
  });

  return visibleRows
    .map((timeline, index) => ({ index, timeline }))
    .sort((left, right) => {
      const dateDifference = `${right.timeline.date || ''}`.localeCompare(`${left.timeline.date || ''}`);
      if (dateDifference !== 0) {
        return dateDifference;
      }

      return left.index - right.index;
    })
    .map(({ timeline }) => timeline);
};

export const getMedcallTimelinePage = <Timeline>(
  timelines: Timeline[],
  page: number,
) => {
  const pageCount = Math.max(1, Math.ceil(timelines.length / MEDCALL_LEGACY_TIMELINE_PAGE_SIZE));
  const normalizedPage = Math.min(Math.max(1, page), pageCount);
  const startIndex = (normalizedPage - 1) * MEDCALL_LEGACY_TIMELINE_PAGE_SIZE;

  return {
    page: normalizedPage,
    pageCount,
    rows: timelines.slice(startIndex, startIndex + MEDCALL_LEGACY_TIMELINE_PAGE_SIZE),
  };
};
